import * as yup from "yup";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { API_DATE_FORMAT, HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";

export const ORDER_BY = {
  name: "name",
  vendor: "vendor",
  type: "type",
  netQuantity: "net_quantity",
  grossSales: "gross_sales",
  discounts: "discounts",
  returns: "returns",
  netSales: "net_sales",
  tax: "tax",
};

const requestSchema = yup.object({
  search: yup.string().nullable(),
  period: yup
    .object({
      from: yup.date().nullable(),
      to: yup.date().nullable(),
    })
    .nullable(),
  order: yup
    .object({
      by: yup.string().required(),
      direction: yup.string().required(),
    })
    .nullable(),
});

const responseSchema = yup.object({
  reports: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        vendor: yup.string().required(),
        type: yup.string().nullable(),
        net_quantity: yup.number().required(),
        gross_sales: yup.string().required(),
        net_sales: yup.string().required(),
        returns: yup.string().required(),
        discounts: yup.string().required(),
        tax: yup.string().required(),
      }),
    )
    .required(),
});

const composeResponse = (res) => {
  const reports = res?.data?.data?.report_data || [];

  return {
    reports,
  };
};

const createShopifyProductSalesReportQueryKey = (...args) => [
  QUERY_KEYS.shopifyProductSalesReport.get,
  ...args,
];

export function useShopifyProductSalesReportQuery(payload = {}, options = {}) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  const fromDate = unwrapOr(
    () => moment(req.period.from).format(API_DATE_FORMAT),
    null,
  );

  const toDate = unwrapOr(
    () => moment(req.period.to).format(API_DATE_FORMAT),
    null,
  );

  return useQuery({
    queryKey: createShopifyProductSalesReportQueryKey(
      fromDate,
      toDate,
      req.order?.by,
      req.order?.direction,
      req.search,
    ),
    queryFn: async () => {
      const res = await http.post(
        HTTP_ENDPOINTS.shopifyProductSalesReport.get(),
        removeNullishFromShape({
          fromDate,
          toDate,
          term: req.search || undefined,
          sortBy: req.order?.by,
          sortOrder: req.order?.direction,
        }),
      );
      return responseSchema.validateSync(composeResponse(res), {
        strict: true,
      });
    },
    ...options,
  });
}
